<template>
  <Card>
    <template #title>Pengaturan Printer</template>
    <template #subtitle
      >Pastikan perangkat Bluetooth terhubung dengan benar.</template
    >
    <template #content>
      <div
        class="flex flex-wrap items-center justify-center gap-3 p-4 bg-gray-50 dark:bg-dark-800 rounded-lg mb-6"
      >
        <div class="flex items-center gap-2">
          <i class="pi pi-print text-lg"></i>
          <span class="font-medium">Printer Bluetooth:</span>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <Button
            :icon="isConnected ? 'pi pi-check-circle' : 'pi pi-bluetooth'"
            :label="
              isConnected ? `Terkoneksi: ${deviceName}` : 'Sambungkan Printer'
            "
            :severity="isConnected ? 'success' : undefined"
            :loading="isConnecting"
            :disabled="isConnecting"
            @click="connectBluetooth"
            class="min-w-[200px]"
            :class="!isConnected ? '!bg-primary !border-primary hover:!bg-hover-primary !text-white' : ''"
          />
          <Button
            v-if="isConnected"
            icon="pi pi-times"
            label="Putuskan"
            severity="primary"
            outlined
            @click="disconnectBluetooth"
            :disabled="isConnecting"
          />
        </div>
        <div v-if="connectionError" class="w-full text-red-500 text-sm mt-2">
          {{ connectionError }}
        </div>
      </div>
    </template>
  </Card>
  <Card>
    <template #title>Print Test</template>
    <template #subtitle>Pastikan Printer Sudah Terkoneksi.</template>
    <template #content>
      <div class="grid grid-cols-2 gap-2">
        <Button
          icon="pi pi-print"
          aria-label="Print"
          @click="printDocument('bill')"
          label="Print Bill"
          severity="info"
          :disabled="!isConnected"
        />
        <Button
          icon="pi pi-print"
          aria-label="Print"
          @click="printDocument('receipt')"
          label="Print Receipt"
          severity="primary"
          :disabled="!isConnected"
        />
      </div>
    </template>
  </Card>

  <!-- Print sections with print-specific styles -->
  <div class="print-section" id="receipt" :class="{ hidden: true }">
    <ReceiptPrintNoJSP
      store-name="Erista Dwatiga"
      store-address="jalan Semarang"
      store-phone="085104116711"
      :subtotal="10000"
    />
  </div>
  <div class="print-section" id="bill" :class="{ hidden: true }">
    <BillPrint
      store-name="Erista Dwatiga"
      store-address="jalan Semarang"
      store-phone="085104116711"
      :subtotal="10000"
    />
  </div>
</template>

<script>
import { defineComponent } from "vue";
import { mapState, mapActions, mapGetters } from "vuex";
import ReceiptPrintNoJSP from "@/components/print/receipt/ReceiptPrintNoJSP.vue";
import { Button, Card } from "primevue";
import BillPrint from "@/components/print/receipt/BillPrint.vue";
import print from "@/helpers/print";
import { selectedQris } from "@/helpers/qris";

export default defineComponent({
  name: "PrintTes",
  components: {
    Button,
    Card,
    ReceiptPrintNoJSP,
    BillPrint,
  },
  data() {
    return {
      kitchenDivision: null,
      isEdit: false,
      idKitchenDivision: null,
      printerIP: "",
      outletId: "",
      validation: {
        printer_ip: {
          valid: true,
          message: "",
        },
      },
      QRIS: selectedQris(new Date()),
      hint: {
        printer_ip: ["Contoh: 237.84.2.178"],
      },
      field: ["printer_ip"],
    };
  },
  computed: {
    ...mapGetters("kitchen_divisions", ["allKitchenDivisions"]),
    ...mapGetters("outlets", ["allOutlets"]),
    ...mapState("bluetooth", ["isConnecting", "error"]),
    ...mapGetters("bluetooth", ["isConnected", "deviceName"]),
    connectionError() {
      return this.error?.message || null;
    },
  },
  watch: {
    allKitchenDivisions(newVal) {
      if (newVal.length > 0) {
        this.printerIP = newVal[0].printer_ip;
        this.isEdit = true;
        this.idKitchenDivision = newVal[0].id;
      } else {
        this.printerIP = "";
        this.isEdit = false;
        this.idKitchenDivision = null;
      }
    },
    allOutlets(newVal) {
      if (newVal.length > 0) {
        this.kitchenDivision = newVal[0].name;
        this.outletId = newVal[0].id;
      } else {
        this.outletId = "";
        this.kitchenDivision = null;
      }
    },
  },
  async created() {
    await this.$store.dispatch("kitchen_divisions/fetchKitchenDivisions");
    await this.$store.dispatch("outlets/fetchOutlets");
  },
  methods: {
    ...mapActions("kitchen_divisions", [
      "createKitchenDivision",
      "updateKitchenDivision",
    ]),
    ...mapActions("bluetooth", [
      "connectBluetooth",
      "disconnectBluetooth",
      "sendToPrinter",
    ]),

    async printDocument(type) {
      if (!this.isConnected) {
        this.$toast.add({
          severity: "warn",
          summary: "Tidak Terhubung",
          detail: "Silakan sambungkan printer Bluetooth terlebih dahulu",
          life: 3000,
        });
        return;
      }

      try {
        // Get the content to print
        const printSection = document.getElementById(type);
        if (!printSection) {
          throw new Error(`Print section ${type} not found`);
        }

        // Print to Bluetooth
        await this.printToBluetooth(type);

        this.$toast.add({
          severity: "success",
          summary: "Terkirim ke Printer",
          detail: "Dokumen berhasil dikirim ke printer",
          life: 3000,
        });
      } catch (error) {
        console.error("Print error:", error);
        this.$toast.add({
          severity: "error",
          summary: "Gagal Mencetak",
          detail: error.message,
          life: 5000,
        });
      }
    },

    async printToBluetooth(receiptType = "bill") {
      try {
        // Get the formatted receipt content
        const formattedContent = this.formatElementForPrinting(
          receiptType === "receipt" ? "receipt-print" : "bill-print"
        );

        // Convert content to Uint8Array (ESC/POS commands)
        const encoder = new TextEncoder("utf-8");

        // Initialize printer commands
        const initCmds = new Uint8Array([
          0x1b,
          0x40, // Initialize printer
          0x1b,
          0x61,
          0x01, // Center align
        ]);

        // Send initialization commands first
        await this.sendInChunks(initCmds, 100);
        await new Promise((r) => setTimeout(r, 100));

        // Send receipt content in chunks
        const contentBytes = encoder.encode(formattedContent);
        await this.sendInChunks(contentBytes, 100);
        await new Promise((r) => setTimeout(r, 100));

        // Add QRIS section
        if (this.QRIS?.code_qris) {
          // Add QRIS header
          const qrHeader = "\n=== QRIS PAYMENT ===\n";
          await this.sendInChunks(encoder.encode(qrHeader), 100);
          await new Promise((r) => setTimeout(r, 100));

          // Add QRIS name
          const qrName = `Bank: ${this.QRIS.name}\n`;
          await this.sendInChunks(encoder.encode(qrName), 100);
          await new Promise((r) => setTimeout(r, 100));

          // Add QR code
          const qrText = this.QRIS.code_qris;
          const qrCode = print.buildQrCodeCommand(qrText);

          // Send QR code with proper initialization
          const qrInit = new Uint8Array([
            0x1d,
            0x28,
            0x6b,
            0x03,
            0x00,
            0x31,
            0x43,
            0x08, // Set QR code size
            0x1d,
            0x28,
            0x6b,
            0x03,
            0x00,
            0x31,
            0x45,
            0x31, // Set QR code error correction
          ]);

          await this.sendInChunks(qrInit, 100);
          await this.sendInChunks(qrCode, 100);
          await new Promise((r) => setTimeout(r, 300)); // Give more time for QR code processing

          // Add footer after QR code
          const qrFooter = "\nScan QRIS untuk pembayaran\n";
          await this.sendInChunks(encoder.encode(qrFooter), 100);
          await new Promise((r) => setTimeout(r, 100));
        } else {
          console.warn("No QRIS code available for printing");
        }

        // Add line feeds and cut paper
        const endCmds = new Uint8Array([
          0x0a,
          0x0a,
          0x0a,
          0x0a, // Line feeds
          0x1d,
          0x56,
          0x41,
          0x10, // Cut paper
        ]);

        await this.sendInChunks(endCmds, 100);
      } catch (error) {
        console.error("Print error:", error);
        throw error;
      }
    },

    async sendInChunks(data, chunkSize = 500) {
      if (!this.isConnected) {
        throw new Error("Printer tidak terhubung");
      }

      const chunks = [];
      for (let i = 0; i < data.length; i += chunkSize) {
        chunks.push(data.slice(i, i + chunkSize));
      }

      for (const chunk of chunks) {
        await this.sendToPrinter(chunk);
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
    },

    formatElementForPrinting(divId) {
      let printText = "";
      const rows = [];

      // Add company header with center alignment
      rows.push("\x1b\x61\x01"); // Center alignment
      rows.push(process.env.VUE_APP_STORE_NAME);
      rows.push(process.env.VUE_APP_STORE_ADDRESS);
      rows.push(process.env.VUE_APP_STORE_PHONE);
      rows.push(print.breakLine(40, "="));

      // Add transaction details
      rows.push(print.twoColumn(`No. Invoice:`, `SOD000001`, 17, 6, 17));
      rows.push(
        print.twoColumn(
          `Tanggal:`,
          `${print.formatDate(new Date())}`,
          17,
          6,
          17
        )
      );
      rows.push(
        print.twoColumn(`Nama Tamu:`, `Galang Luhur Pekerti`, 17, 6, 17)
      );
      rows.push(print.twoColumn(`Kasir:`, `Tiara Khoirunnisa`, 17, 6, 17));
      rows.push(print.twoColumn(`Staff:`, `Tiara Khoirunnisa`, 17, 6, 17));
      rows.push(print.breakLine(40, "="));

      // Add order items
      for (let index = 0; index < 3; index++) {
        rows.push(print.oneLine(`POS Online`, 40));
        rows.push(print.twoColumn(`  1 x Rp 20.000`, `Rp 20.000`, 17, 6, 17));
        rows.push(print.twoColumn(`  Discount:`, `-Rp 0`, 17, 6, 17));
        rows.push(print.twoColumn(`  Global Discount:`, `-Rp 0`, 17, 6, 17));
      }

      rows.push(print.breakLine(40, "-"));

      // Add payment details with proper spacing
      rows.push(print.twoColumn(`Sub Total:`, `Rp 60.000`, 17, 6, 17));

      rows.push(print.twoColumn(`Diskon:`, `-Rp 0`, 17, 6, 17));
      rows.push(print.twoColumn(`Global Discount:`, `-Rp 0`, 17, 6, 17));
      rows.push(print.twoColumn(`Service Charge 0%:`, `Rp 0`, 17, 6, 17));
      rows.push(print.twoColumn(`Tax ${Number(0)}%:`, `Rp 0`, 17, 6, 17));
      rows.push(print.twoColumn(`Total:`, `Rp 60.000`, 17, 6, 17));

      // Add payment info if paid
      rows.push(print.breakLine(40, "-"));
      rows.push(print.twoColumn(`Payment Method:`, `QRIS`, 17, 6, 17));

      // Add QRIS NMID as barcode if available
      if (this.QRIS?.nmid) {
        rows.push(print.breakLine(40, " "));
        rows.push("\x1b\x61\x01"); // Center alignment
        rows.push("QRIS - " + this.QRIS.name);
        rows.push("\x1b\x61\x00"); // Left alignment
        rows.push(print.breakLine(40, " "));

        // Add NMID barcode
        const nmid = this.QRIS.nmid.toString();
        rows.push("\x1d\x68\x64"); // Set barcode height
        rows.push("\x1d\x77\x03"); // Set barcode width
        rows.push("\x1d\x6b\x45" + String.fromCharCode(nmid.length) + nmid); // Print barcode (CODE128)
        rows.push(nmid); // Human readable NMID
        rows.push(print.breakLine(40, " "));
      }

      rows.push(print.twoColumn(`Paid Amount:`, `Rp 60.000`, 17, 6, 17));
      rows.push(print.twoColumn(`Change:`, `Rp 0`, 17, 6, 17));

      // Add footer with center alignment
      rows.push("\x1b\x61\x01"); // Center alignment
      rows.push(print.breakLine(40, "="));

      if (divId === "receipt-print") {
        rows.push("Terima Kasih Atas Kunjungan Anda");
        rows.push(
          print.centerTextByCharacter(
            "Simpan Struk ini sebagai bukti pembayaran",
            40
          )
        );
      } else {
        rows.push("Terima Kasih Atas Pembelian Anda!");
      }

      rows.push(print.breakLine(40, "="));
      rows.push(`Jam Cetak: \t ${new Date().toLocaleString()}`);
      rows.push("\n\n\n"); // Extra lines for paper cut

      // Join all rows with newlines
      printText = rows.join("\n");

      return printText;
    },

    submitKitchenDivision() {
      this.$confirm.require({
        group: "headless",
        header: "Apakah anda yakin ingin menyimpan printer?",
        message: "Silahkan konfirmasi sebelum diproses.",
        acceptLabel: "Ya",
        rejectLabel: "Tidak",
        accept: () => {
          this.postKitchenDivision();
        },
        reject: () => {
          this.$toast.add({
            severity: "error",
            summary: "Rejected",
            detail: "Kamu telah membatalkan aksi simpan kitchen division",
            life: 3000,
          });
        },
      });
    },

    async postKitchenDivision() {
      try {
        const typeData = {
          name: this.kitchenDivision,
          printer_ip: this.printerIP,
        };

        if (this.isEdit && this.kitchenDivision) {
          typeData.id = this.idKitchenDivision;
          await this.updateKitchenDivision(typeData);
        } else {
          await this.createKitchenDivision(typeData);
        }

        this.$toast.add({
          severity: "success",
          summary: "Success",
          detail: "Printer berhasil disimpan",
          life: 5000,
        });
      } catch (error) {
        console.error("Failed to save transaction type:", error);

        if (error.status === 400 && error.response.data.errors) {
          this.validationErrors(error.response.data.errors);
          this.$toast.add({
            severity: "error",
            summary: "Failed",
            detail: error.response.data.message,
            life: 5000,
          });
        } else {
          this.$toast.add({
            severity: "error",
            summary: "Rejected",
            detail: error.response.data.message,
            life: 5000,
          });
        }
      }
    },

    validationErrors(errorMessage) {
      for (let index = 0; index < this.field.length; index++) {
        this.validation[this.field[index]].valid = true;
        this.validation[this.field[index]].message = "";
      }

      errorMessage.forEach((error) => {
        if (this.validation[error.field]) {
          this.validation[error.field].valid = false;
          this.validation[error.field].message = error.message;
        }
      });
    },
  },
});
</script>

<style>
/* Print-specific styles */
@media print {
  body * {
    visibility: hidden;
  }

  .print-section,
  .print-section * {
    visibility: visible;
  }

  .print-section {
    position: absolute;
    left: 0;
    top: 0;
  }

  @page {
    margin: 0;
    size: 80mm 297mm;
    /* For thermal receipt printer - adjust size as needed */
  }
}

.hidden {
  display: none;
}

/* Regular screen styles */
.print-section {
  display: none;
}

/* Add Bluetooth connection status indicator */
.bluetooth-status {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 5px;
}

.bluetooth-connected {
  background-color: #22c55e;
}

.bluetooth-disconnected {
  background-color: #ef4444;
}

/* Make buttons full width on mobile */
@media (max-width: 640px) {
  .grid-cols-2 {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
}
</style>
